import type { ButtonSchemaType, ButtonFontSchemaType } from '@vedalib/editor/lib/brand'
import type {
  EditorElement,
  ButtonElementValue,
  ButtonCustomNavigationData,
} from '@vedalib/editor/lib/elements'
import {
  ButtonActionType,
  ButtonBlockAction,
  ButtonNavigationAction,
} from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import lodash from 'lodash'
import { useMemo } from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import type { IFieldConfig } from 'components/formKit'
import { FieldFile, FieldInput, FieldSegmented, FieldSelect } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import type { IFormKitControlProps } from 'components/formKit/types'
import { isMixed } from 'components/uiKit/KitUtils'
import { INVERTED_DEFAULT_OPTIONS } from 'components/uiKit/Segmented/Segmented'
import { ISelectProps } from 'components/uiKit/Select'
import { AppMode, BlockMode, SectionTypeEnum } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlocks, getEditorMode, getSection, getSections } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { getParent } from 'utils/form'
import { useNextSection } from 'utils/project.hooks'
import { linkUrlValidation } from 'utils/websiteValidation'

import {
  filterByBlockAction,
  filterByNavigation,
  getButtonActionTypesByMode,
  getLabelVisibility,
  valueByAction,
  valueByType,
} from './helper'

type ButtonFieldContext = 'main' | 'correct' | 'incorrect'

const getButtonFieldContext = (name: string): ButtonFieldContext => {
  if (name.includes('incorrect')) {
    return 'incorrect'
  }
  if (name.includes('correct')) {
    return 'correct'
  }
  return 'main'
}

const getDepthByContext = (context: ButtonFieldContext, baseDepth: number) =>
  context === 'main' ? baseDepth : baseDepth + 1

const getConfigByContext = (context: ButtonFieldContext, element?: ButtonsElementType) => {
  if (!element?.value) {
    return undefined
  }
  if (context === 'correct') {
    return element.value.correct
  }
  if (context === 'incorrect') {
    return element.value.incorrect
  }
  return element.value
}

type ButtonsElementType = EditorElement<ButtonElementValue, ButtonSchemaType, ButtonFontSchemaType>

const useShowLink = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 3)
  const element = useFormData<ButtonsElementType>(name, depth)
  const actionType = getConfigByContext(context, element)?.actionType
  const hidden = actionType !== ButtonActionType.link
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowFile = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 3)
  const element = useFormData<ButtonsElementType>(name, depth)
  const actionType = getConfigByContext(context, element)?.actionType
  const hidden = actionType !== ButtonActionType.file
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowCourseStart = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const element = useFormData<ButtonsElementType>(name, depth)
  const config = getConfigByContext(context, element)
  const hidden =
    config?.action !== ButtonBlockAction.courseStart || Boolean(element?.value?.item?.value)
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowContinue = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const element = useFormData<ButtonsElementType>(name, depth)
  const actionType = getConfigByContext(context, element)?.actionType
  const hidden = actionType !== ButtonActionType.continue
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowBlock = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const element = useFormData<ButtonsElementType>(name, depth)
  const actionType = getConfigByContext(context, element)?.actionType
  const hidden = actionType !== ButtonActionType.continue
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowTarget = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 3)
  const element = useFormData<ButtonsElementType>(name, depth)
  const actionType = getConfigByContext(context, element)?.actionType
  const hidden = actionType !== ButtonActionType.link
  return useMemo(() => ({ hidden }), [hidden])
}

const VARIANT_EXCLUDED_ACTION_TYPES = [ButtonActionType.decision, ButtonActionType.continue]

const useActionType = ({
  name,
}: IFieldConfig<Block>): Partial<IFieldConfig<Block, ISelectProps<ButtonActionType>>> => {
  const block = useFormData<Block>()
  const section = useProjectContext(getSection)
  const context = getButtonFieldContext(name)
  return useMemo(
    () => ({
      props: {
        options: getButtonActionTypesByMode(section?.type, block.mode).filter((o) => {
          if (context === 'main') {
            return true
          }
          return !VARIANT_EXCLUDED_ACTION_TYPES.includes(o.value)
        }),
      },
      effect: ({ name, value }) => {
        const { parentName, parent } = getParent<ButtonElementValue>(name, block)
        const val =
          value && !isMixed(value) ? valueByType[value as keyof typeof valueByType] : undefined
        let newVal: Partial<ButtonElementValue> = val || {
          actionType: isMixed(value) ? undefined : value,
        }

        if (value === ButtonActionType.decision) {
          const variantDefault =
            block?.mode === BlockMode.end
              ? valueByAction[ButtonBlockAction.testEnd]
              : valueByAction[ButtonBlockAction.validate]
          newVal = { ...newVal, correct: variantDefault, incorrect: variantDefault }
        }

        if (value === ButtonActionType.blockAction) {
          const defaultAction =
            block?.mode === BlockMode.end
              ? valueByAction[ButtonBlockAction.testEnd]
              : valueByAction[ButtonBlockAction.validate]
          newVal = { ...defaultAction }
        }

        const newChangeData = { name: parentName, value: { item: parent?.item, ...newVal } }
        return newChangeData
      },
    }),
    [section?.type, block, context],
  )
}
const ACTION_HIDDEN_TYPES = [
  ButtonActionType.link,
  ButtonActionType.file,
  ButtonActionType.continue,
  ButtonActionType.decision,
]

const useAction = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const section = useProjectContext(getSection)
  const block = useFormData<Block>()
  const mode = block?.mode
  const sectionType = section?.type
  const depth = getDepthByContext(context, 2)
  const { parent: element } = getParent<ButtonsElementType>(name, block, depth)
  const type = getConfigByContext(context, element)?.actionType
  const blockAction = type === ButtonActionType.blockAction

  const filteredOptions = useMemo(() => {
    const options = blockAction
      ? filterByBlockAction({ mode, sectionType })
      : filterByNavigation({ mode })
    return options
  }, [blockAction, mode, sectionType])

  return useMemo(
    () => ({
      hidden: type ? ACTION_HIDDEN_TYPES.includes(type) : false,
      props: { options: filteredOptions },
    }),
    [filteredOptions, type],
  )
}

const SECTIONS_FOR_CUSTOM_NAVIGATION = [
  SectionTypeEnum.cover,
  SectionTypeEnum.landing,
  SectionTypeEnum.test,
]

const useCustomNavigationSections = ({ name }: IFieldConfig<Block>) => {
  const sections = useProjectContext(getSections, SECTIONS_FOR_CUSTOM_NAVIGATION)
  const block = useFormData()

  return useMemo(() => {
    const sectionOptions = sections
      .filter(({ type }) => type !== SectionTypeEnum.chapter)
      .map((section) => ({ value: section.id, label: section.name }))
    const context = getButtonFieldContext(name)
    const depth = getDepthByContext(context, 3)
    const { parent } = getParent<ButtonsElementType>(name, block, depth)
    const action = getConfigByContext(context, parent)?.action
    const hidden = action !== ButtonNavigationAction.custom
    return { hidden, props: { options: sectionOptions } }
  }, [sections, name, block])
}

const useCustomNavigationBlocks = ({ name }: IFieldConfig<Block>) => {
  const block = useFormData<Block>()
  const sections = useProjectContext(getSections, SECTIONS_FOR_CUSTOM_NAVIGATION)

  return useMemo(() => {
    const { parent } = getParent<ButtonCustomNavigationData>(name, block)
    const section = sections.find(({ id }) => id === parent?.sectionId)
    const options = section?.blocksOrder.map((value, i) => ({
      value,
      label: `${i + 1} ${t('elements.button.form.blockCount')}`,
      disabled: value === block?.uuid,
    }))

    return {
      hidden:
        !parent?.sectionId ||
        section?.type === SectionTypeEnum.test ||
        section?.type === SectionTypeEnum.cover,
      props: { options },
    }
  }, [sections, block, name])
}

const useActionChangeEffect = (): Partial<
  IFieldConfig<Block, IFormKitControlProps<ButtonBlockAction | ButtonNavigationAction>>
> => {
  const block = useFormData<Block>()
  return useMemo(
    () => ({
      effect: ({ name, value }) => {
        const { parentName, parent } = getParent<ButtonsElementType['value']>(name, block)
        const val =
          value && !isMixed(value) ? valueByAction[value as keyof typeof valueByAction] : undefined
        const newVal = val || { ...lodash.pick(parent, ['actionType']), action: value }
        const newChangeData = { name: parentName, value: { item: parent?.item, ...newVal } }
        return newChangeData
      },
    }),
    [block],
  )
}

const LABEL_SECTIONS = [SectionTypeEnum.cover, SectionTypeEnum.landing, SectionTypeEnum.test]

const useLabelVisibilityParams = (name: string) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const block = useFormData<Block>()
  const element = useFormData<ButtonsElementType>(name, depth)
  const section = useProjectContext(getSection)
  const sections = useProjectContext(getSections, LABEL_SECTIONS)
  const blocks = useProjectContext(getBlocks, section?.id)
  const isProMode = useProjectContext(getEditorMode) === AppMode.pro
  const nextSection = useNextSection(section?.id, sections)

  return useMemo(() => {
    const action = getConfigByContext(context, element)?.action
    const indexCurrentBlock = blocks.findIndex((b) => b.uuid === block?.uuid)
    const isNextBlockQuestions = blocks[indexCurrentBlock + 1]?.mode === BlockMode.questions
    return { action, section, block, isProMode, nextSection, isNextBlockQuestions }
  }, [element, context, blocks, section, block, isProMode, nextSection])
}

const useSecondLabel = ({ name }: IFieldConfig<Block>) => {
  const { action, section, block, isProMode, nextSection, isNextBlockQuestions } =
    useLabelVisibilityParams(name)

  return useMemo(() => {
    const { secondLabel } = getLabelVisibility({
      action,
      isProMode,
      blockMode: block?.mode,
      sectionType: section?.type,
      nextSection: Boolean(nextSection),
      isNextBlockQuestions,
    })
    return { hidden: !secondLabel }
  }, [action, block?.mode, isProMode, nextSection, section?.type, isNextBlockQuestions])
}

const useThirdLabel = ({ name }: IFieldConfig<Block>) => {
  const { action, section, block, isProMode, nextSection, isNextBlockQuestions } =
    useLabelVisibilityParams(name)

  return useMemo(() => {
    const { secondLabel, thirdLabel } = getLabelVisibility({
      action,
      isProMode,
      blockMode: block?.mode,
      sectionType: section?.type,
      nextSection: Boolean(nextSection),
      isNextBlockQuestions,
    })
    const secondLabelCount = secondLabel ? 1 : 0
    const labelNumber = 2 + secondLabelCount
    return { hidden: !thirdLabel, label: `${t('elements.button.form.value')} ${labelNumber}` }
  }, [action, block?.mode, isProMode, nextSection, section?.type, isNextBlockQuestions])
}

const useFourthLabel = ({ name }: IFieldConfig<Block>) => {
  const { action, section, block, isProMode, nextSection, isNextBlockQuestions } =
    useLabelVisibilityParams(name)

  return useMemo(() => {
    const { secondLabel, thirdLabel, fourthLabel } = getLabelVisibility({
      action,
      isProMode,
      blockMode: block?.mode,
      sectionType: section?.type,
      nextSection: Boolean(nextSection),
      isNextBlockQuestions,
    })
    const secondLabelCount = secondLabel ? 1 : 0
    const thirdLabelCount = thirdLabel ? 1 : 0
    const labelNumber = 2 + secondLabelCount + thirdLabelCount
    return { hidden: !fourthLabel, label: `${t('elements.button.form.value')} ${labelNumber}` }
  }, [action, block?.mode, isProMode, nextSection, section?.type, isNextBlockQuestions])
}

const useVariantBlockActionHidden = (
  { name }: IFieldConfig<Block>,
  actions: ButtonBlockAction[],
) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const element = useFormData<ButtonsElementType>(name, depth)
  const config = getConfigByContext(context, element)
  const hidden =
    context !== 'main' &&
    config?.actionType === ButtonActionType.blockAction &&
    actions.includes(config.action as ButtonBlockAction)
  return useMemo(() => ({ hidden }), [hidden])
}

const useLabelHidden = (fieldConfig: IFieldConfig<Block>) =>
  useVariantBlockActionHidden(fieldConfig, [ButtonBlockAction.validate])

const useFirstLabelHidden = (fieldConfig: IFieldConfig<Block>) =>
  useVariantBlockActionHidden(fieldConfig, [ButtonBlockAction.validate])

const useDecisionEndLabelHidden = ({ name }: IFieldConfig<Block>) => {
  const context = getButtonFieldContext(name)
  const depth = getDepthByContext(context, 2)
  const element = useFormData<ButtonsElementType>(name, depth)
  const block = useFormData<Block>()
  const config = getConfigByContext(context, element)
  const section = useProjectContext(getSection)
  const hidden =
    context === 'main' &&
    section?.type === SectionTypeEnum.test &&
    config?.actionType === ButtonActionType.decision &&
    block?.mode === BlockMode.end
  return useMemo(() => ({ hidden }), [hidden])
}

export const SECOND_LABEL = genElementValueField({
  as: FieldInput,
  name: 'secondLabel',
  label: `${t('elements.button.form.value')} 2`,
  rules: [{ max: 90 }],
  useConfigs: [useSecondLabel],
})

export const THIRD_LABEL = genElementValueField({
  as: FieldInput,
  name: 'thirdLabel',
  label: `${t('elements.button.form.value')} 3`,
  rules: [{ max: 90 }],
  useConfigs: [useThirdLabel],
})

export const FOURTH_LABEL = genElementValueField({
  as: FieldInput,
  name: 'fourthLabel',
  label: `${t('elements.button.form.value')} 4`,
  rules: [{ max: 90 }],
  useConfigs: [useFourthLabel, useLabelHidden],
})

export const LABEL = genElementValueField({
  as: FieldInput,
  name: 'label',
  label: t('elements.button.form.value'),
  rules: [{ max: 90 }],
  useConfigs: [useFirstLabelHidden, useDecisionEndLabelHidden],
})

export const URL = genElementValueField({
  as: FieldInput,
  name: 'actionData.url',
  label: t('elements.button.form.url'),
  rules: [{ validator: linkUrlValidation }],
  useConfigs: [useShowLink],
})

export const FILE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'actionData.file',
  props: {
    fileType: 'document',
    nullable: true,
    preview: true,
    hasAccessibility: true,
  },
  useConfigs: [useShowFile],
})

export const ACTION_TYPE = genElementValueField({
  as: FieldSelect<ButtonActionType>,
  name: 'actionType',
  label: t('input.label.type'),
  props: { fluid: true },
  useConfigs: [useActionType],
})

export const SHOW_BLOCK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showBlock',
  label: t('input.label.hideBlockAfterAction'),
  defaultValue: false,
  props: { options: INVERTED_DEFAULT_OPTIONS },
  useConfigs: [useShowBlock],
})

export const ACTION = genElementValueField({
  as: FieldSelect<ButtonBlockAction | ButtonNavigationAction>,
  name: 'action',
  label: t('input.label.actions'),
  props: { fluid: true },
  useConfigs: [useAction, useActionChangeEffect],
})

export const SECTION_NAVIGATION = genElementValueField({
  as: FieldSelect<string>,
  name: 'actionData.sectionId',
  label: t('elements.button.form.section'),
  props: { fluid: true, dropdownType: 'dropdown' },
  useConfigs: [useCustomNavigationSections],
  effect: (fieldChange) => {
    const { parentName } = getParent(fieldChange.name)
    const newValue = { sectionId: fieldChange.value }
    return { name: parentName, value: newValue }
  },
})

export const BLOCK_NAVIGATION = genElementValueField({
  as: FieldSelect<string>,
  name: 'actionData.blockId',
  label: t('elements.button.form.block'),
  props: { fluid: true, placeholder: t('input.option.start') },
  useConfigs: [useCustomNavigationBlocks],
})

export const POSITION = genElementValueField({
  as: FieldSelect<'hide' | 'left' | 'right'>,
  name: 'position',
  label: t('input.label.icon'),
  defaultValue: 'hide',
  props: {
    fluid: true,
    options: [
      { value: 'hide', label: t('input.option.withoutIcon') },
      { value: 'left', label: t('input.option.left') },
      { value: 'right', label: t('input.option.right') },
    ],
  },
  useConfigs: [useShowCourseStart],
})

export const CONDITION = genElementValueField({
  as: FieldSelect<'none' | 'all' | 'last'>,
  name: 'condition',
  label: t('elements.button.form.condition'),
  defaultValue: 'none',
  props: {
    options: [
      { value: 'none', label: t('input.option.no') },
      { value: 'all', label: t('input.option.allBlockViewed') },
      { value: 'last', label: t('input.option.blockViewed') },
    ],
    fluid: true,
  },
  useConfigs: [useShowContinue],
})

export const TARGET = genElementValueField({
  as: FieldSegmented<'_blank' | '_self'>,
  name: 'actionData.target',
  label: t('elements.button.form.target'),
  defaultValue: '_blank',
  props: {
    options: [
      { value: '_blank', label: t('input.option.no') },
      { value: '_self', label: t('input.option.yes') },
    ],
  },
  useConfigs: [useShowTarget],
})
